import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Search, Settings, X } from 'lucide-react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import useTodayEvents from '../dashboard/useTodayEvents';
import useDashboardTickets from '../dashboard/useDashboardTickets';
import CompactEventList from '../CompactEventList';
import DashboardTicketList, { sichtbareDashboardTickets } from '../DashboardTicketList';
import MobilTicketDetail from './MobilTicketDetail';
import AssistentChat from '../assistent/AssistentChat'; // #512: derselbe Assistent wie am Arbeitsplatz
import KayCeeKopf from '../header/KayCeeKopf';
import MobilTermin from './MobilTermin';
import MobilEinstellungen from './MobilEinstellungen';
import { useFeature } from '../../utils/featuresCache';
import CreateTicketModal, { useCreateTicket } from '../dashboard/CreateTicket';
import { verkleinereFoto } from '../../utils/bildVerkleinern';
import { sichtbareReiter, startReiter, zaehleHeuteGeplanteTickets } from '../../utils/mobilModus';
import './mobil.css';
import Wortmarke from '../header/Wortmarke';

// Mobilansicht für Handys: drei Seiten nebeneinander, gewechselt per Wischen
// oder über die Reiterleiste -- heute geplant, Meine Tickets, Pool.
//
// Daten und Filter kommen aus denselben Hooks wie im Dashboard
// (useTodayEvents, useDashboardTickets), die Listen aus denselben
// Komponenten. Ein Ticket zeigt hier also genau dann, wenn es auch am
// Arbeitsplatz in der entsprechenden Spalte steht.
//
// Suchfeld über einer Ticketliste, wie im Dashboard. Gefiltert wird in
// DashboardTicketList (searchFilter), also nach denselben Feldern.
const Suchfeld = ({ wert, onChange, label }) => {
  const { t } = useI18n();
  return (
    <div className="mobil-suche">
      <label className="mobil-suche-feld">
        <Search size={18} aria-hidden="true" />
        <input
          // type="text" statt "search": sonst zeigt Chrome ein zweites, eigenes ✕
          type="text"
          inputMode="search"
          value={wert}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('dashboard.searchTicketsPlaceholder', 'Tickets durchsuchen...')}
          aria-label={label}
          enterKeyHint="search"
        />
        {wert && (
          <button type="button" className="mobil-suche-leeren" onClick={() => onChange('')}
            aria-label={t('mobile.clearSearch', 'Suche leeren')} title={t('mobile.clearSearch', 'Suche leeren')}>
            <X size={18} />
          </button>
        )}
      </label>
    </div>
  );
};

// Das Ticket öffnet sich über ?t=<id> in der Adresse. So schließt die
// Zurück-Taste des Handys das Ticket, und die Liste dahinter behält Reiter und
// Scrollstand.

const MobilStart = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [einstellungenOffen, setEinstellungenOffen] = useState(false);
  // KI-Suche: wie im Desktop-Header nur für Manager, und nur, wenn sie auf
  // dieser Installation eingeschaltet ist (atlas: aus).
  const kiSucheAn = useFeature('searchAi');

  const [currentUser, setCurrentUser] = useState(null);
  const [ticketFilterSettings, setTicketFilterSettings] = useState({});
  const [bereit, setBereit] = useState(false);
  const [aktualisiert, setAktualisiert] = useState(false);
  const [aktiverReiter, setAktiverReiter] = useState(null);
  const [eventRefreshKey, setEventRefreshKey] = useState(0);
  const [termin, setTermin] = useState(null);
  // Je Liste ein eigener Suchbegriff; er bleibt beim Öffnen eines Tickets stehen.
  const [sucheMeine, setSucheMeine] = useState('');
  const [suchePool, setSuchePool] = useState('');

  const seitenRef = useRef(null);
  const ersteTicketsRef = useRef([]);
  const startGewaehltRef = useRef(false);
  const angefasstRef = useRef(false);

  // Den Pool sehen nur Manager (#44-Revision 2026-10-19)
  const istManager = !!currentUser?.dbUser?.isManager;
  const reiterListe = useMemo(() => sichtbareReiter(istManager), [istManager]);

  const getCurrentUserEmail = useCallback(() => {
    if (!currentUser) return null;
    return typeof currentUser === 'string' ? currentUser : (currentUser?.userPrincipalName || currentUser?.mail || currentUser?.email);
  }, [currentUser]);

  const {
    events, linkedCalendarEventIds, setLinkedCalendarEventIds,
    calendarEventTicketStatus, setCalendarEventTicketStatus, manualRefreshEvents
  } = useTodayEvents();

  const {
    tickets, setTickets, myTickets, setMyTickets, setOriginalTickets, filterTicketsAssignedToMe, optimisticHiddenIdsRef,
    manualRefreshTickets, ticketsStatus, plannedTicketEntries
  } = useDashboardTickets({ currentUser, getCurrentUserEmail, ticketFilterSettings });

  // Die Refresh-Funktionen wechseln ihre Identität mit dem Benutzer. Der
  // Ladelauf beim Start soll trotzdem nur einmal laufen.
  const refreshRef = useRef({});
  refreshRef.current = { manualRefreshTickets, manualRefreshEvents };

  // "Neues Ticket": derselbe Dialog wie im Dashboard (components/dashboard/
  // CreateTicket.js). Der Hook steht hier, damit ein Entwurf das Öffnen eines
  // Tickets übersteht. Fotos werden vor dem Hochladen verkleinert.
  const anlegen = useCreateTicket({
    manualRefreshTickets: () => refreshRef.current.manualRefreshTickets(),
    setEventRefreshKey,
    dateiVorbereitung: verkleinereFoto
  });

  const ladeVerknuepfungen = useCallback(async () => {
    const now = new Date();
    const startDate = new Date(now); startDate.setDate(now.getDate() - 1); startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(now); endDate.setDate(now.getDate() + 1); endDate.setHours(23, 59, 59, 999);
    try {
      const linked = await apiService.getLinkedItems({ startDate, endDate, timeoutMs: 9000 });
      setLinkedCalendarEventIds(linked.linkedCalendarEventIds || []);
      setCalendarEventTicketStatus(linked.calendarEventTicketStatus || {});
    } catch (_) {
      // Ohne Verknüpfungen zeigt die Liste die Termine trotzdem.
    }
  }, [setLinkedCalendarEventIds, setCalendarEventTicketStatus]);

  useEffect(() => {
    let abgemeldet = false;
    (async () => {
      const [benutzer, filter, ticketListe] = await Promise.all([
        apiService.getCurrentUser().catch(() => null),
        apiService.getTicketFilterSettings().catch(() => null),
        apiService.getTickets().then(r => r?.tickets || []).catch(() => []),
        refreshRef.current.manualRefreshEvents().catch(() => null),
        ladeVerknuepfungen()
      ]);
      if (abgemeldet) return;
      if (filter && filter.success) setTicketFilterSettings({ ...filter.filterSettings, keywords: filter.keywords });
      ersteTicketsRef.current = ticketListe;
      setOriginalTickets(ticketListe);
      setCurrentUser(benutzer);
      setBereit(true);
    })();
    return () => { abgemeldet = true; };
    // Einmal beim Einhängen, siehe refreshRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // <main> hat in App.css 20 px Abstand oben und unten (Luft unter dem
  // Header). Den Header gibt es hier nicht: ohne diese Klasse bliebe oben ein
  // leerer Streifen, und die Seite wäre 40 px zu hoch.
  useEffect(() => {
    document.body.classList.add('mobil-aktiv');
    return () => document.body.classList.remove('mobil-aktiv');
  }, []);

  const springeZu = useCallback((reiter, sanft = true) => {
    const el = seitenRef.current;
    const index = reiterListe.indexOf(reiter);
    setAktiverReiter(reiter);
    if (!el || index < 0) return;
    const ziel = index * el.clientWidth;
    if (typeof el.scrollTo === 'function') el.scrollTo({ left: ziel, behavior: sanft ? 'smooth' : 'auto' });
    else el.scrollLeft = ziel;
  }, [reiterListe]);

  // Startreiter einmal wählen, sobald Benutzer, Tickets und Termine da sind.
  // Hat der Benutzer schon selbst gewischt, bleibt seine Wahl.
  useEffect(() => {
    if (!bereit || startGewaehltRef.current) return;
    startGewaehltRef.current = true;
    const benutzerEmail = getCurrentUserEmail();
    const heuteAnzahl = zaehleHeuteGeplanteTickets({
      events, plannedTicketEntries, statusMap: calendarEventTicketStatus, benutzerEmail
    });
    const meineAnzahl = sichtbareDashboardTickets(
      filterTicketsAssignedToMe(ersteTicketsRef.current), { useAssigneePerspective: true }
    ).length;
    if (angefasstRef.current) return;
    springeZu(startReiter({ heuteAnzahl, meineAnzahl, mitPool: istManager }), false);
  }, [bereit, events, plannedTicketEntries, calendarEventTicketStatus, filterTicketsAssignedToMe, getCurrentUserEmail, springeZu, istManager]);

  const beimScrollen = useCallback(() => {
    const el = seitenRef.current;
    if (!el || !el.clientWidth) return;
    const index = Math.max(0, Math.min(reiterListe.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
    setAktiverReiter(prev => (prev === reiterListe[index] ? prev : reiterListe[index]));
  }, [reiterListe]);

  const aktualisieren = useCallback(async () => {
    setAktualisiert(true);
    try {
      await Promise.all([
        refreshRef.current.manualRefreshTickets().catch(() => null),
        refreshRef.current.manualRefreshEvents().catch(() => null),
        ladeVerknuepfungen()
      ]);
    } finally {
      setEventRefreshKey(k => k + 1);
      setAktualisiert(false);
    }
  }, [ladeVerknuepfungen]);

  // Wie im Dashboard: Wer irgendwo ein Ticket ändert, meldet das hier.
  useEffect(() => {
    const handler = () => { aktualisieren(); };
    window.addEventListener('dashboardEventRefresh', handler);
    return () => window.removeEventListener('dashboardEventRefresh', handler);
  }, [aktualisieren]);

  const offenesTicket = searchParams.get('t');
  // KI-Suche über ?ki=1, damit die Zurück-Taste sie schließt (Muster wie ?t=)
  const kiOffen = searchParams.get('ki') === '1';
  const oeffneKiSuche = () => {
    const naechste = new URLSearchParams(searchParams);
    naechste.set('ki', '1');
    setSearchParams(naechste, { state: { kiAusListe: true } });
  };

  const schliesseKiSuche = () => {
    if (location.state?.kiAusListe) navigate(-1);
    else {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('ki');
      setSearchParams(naechste, { replace: true });
    }
  };
  const offenesTicketRef = useRef(offenesTicket);
  offenesTicketRef.current = offenesTicket;

  const oeffneTicket = useCallback((ticket) => {
    if (!ticket || ticket.id == null) return;
    const naechste = new URLSearchParams(searchParams);
    naechste.set('t', String(ticket.id));
    setSearchParams(naechste, { state: { ausListe: true } });
  }, [searchParams, setSearchParams]);

  const schliesseTicket = useCallback((geaendert) => {
    // Abgeschlossen: sofort aus beiden Listen nehmen und kurz unterdrücken,
    // sonst blendet eine ältere Antwort (SWR-Cache) es wieder ein. Muster aus
    // Dashboard.handleCloseTicketDetail.
    if (geaendert === 'completed' && offenesTicketRef.current) {
      const idStr = String(offenesTicketRef.current);
      optimisticHiddenIdsRef.current.set(idStr, Date.now() + 30000);
      setMyTickets(prev => prev.filter(x => String(x.id) !== idStr));
      setTickets(prev => prev.filter(x => String(x.id) !== idStr));
    }
    // Vom Listenklick geöffnet: ein Schritt zurück, wie die Zurück-Taste.
    // Von außen geöffnet (Benachrichtigung, Link): Parameter entfernen.
    if (location.state?.ausListe) navigate(-1);
    else {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('t');
      setSearchParams(naechste, { replace: true });
    }
    if (geaendert) aktualisieren();
  }, [navigate, location.state, searchParams, setSearchParams, aktualisieren, optimisticHiddenIdsRef, setMyTickets, setTickets]);

  const beimTerminKlick = useCallback((payload) => {
    // CompactEventList reicht ein Ticket oder einen reinen Kalendertermin.
    if (payload && payload.id != null && (payload.ticketNumber != null || payload.status != null || payload.title != null)) {
      oeffneTicket(payload);
      return;
    }
    // Reiner Termin: eigene Handy-Ansicht, über ?termin=1 in der Adresse,
    // damit die Zurück-Taste ihn schließt. Den Termin selbst hält der Zustand.
    if (!payload) return;
    setTermin(payload);
    const naechste = new URLSearchParams(searchParams);
    naechste.set('termin', '1');
    setSearchParams(naechste, { state: { terminAusListe: true } });
  }, [oeffneTicket, searchParams, setSearchParams]);

  const terminOffen = searchParams.get('termin') === '1';

  const schliesseTermin = useCallback(() => {
    if (location.state?.terminAusListe) navigate(-1);
    else {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('termin');
      setSearchParams(naechste, { replace: true });
    }
  }, [location.state, navigate, searchParams, setSearchParams]);

  // Nach dem Neuladen steht ?termin=1 noch in der Adresse, der Termin aber
  // nicht mehr im Zustand: Parameter entfernen. Und umgekehrt den Zustand
  // leeren, wenn die Zurück-Taste den Parameter genommen hat.
  useEffect(() => {
    if (terminOffen && !termin) {
      const naechste = new URLSearchParams(searchParams);
      naechste.delete('termin');
      setSearchParams(naechste, { replace: true });
    } else if (!terminOffen && termin) {
      setTermin(null);
    }
  }, [terminOffen, termin, searchParams, setSearchParams]);

  const benutzerEmail = getCurrentUserEmail();
  const anzahl = useMemo(() => ({
    heute: zaehleHeuteGeplanteTickets({ events, plannedTicketEntries, statusMap: calendarEventTicketStatus, benutzerEmail }),
    meine: sichtbareDashboardTickets(myTickets, { useAssigneePerspective: true }).length,
    pool: sichtbareDashboardTickets(tickets).length
  }), [events, plannedTicketEntries, calendarEventTicketStatus, benutzerEmail, myTickets, tickets]);

  const titel = {
    heute: t('mobile.tabToday', 'Heute'),
    meine: t('mobile.tabMine', 'Meine'),
    pool: t('mobile.tabPool', 'Pool')
  };

  const neuesTicket = () => {
    const { newTicket, setNewTicket, setPendingCreateTicketFiles, setShowCreateTicketForm, availableEmails } = anlegen;
    // Auf "Meine" ist der Bearbeiter man selbst, sonst bleibt das Ticket im
    // Pool. Nur bei leerem Entwurf, eine eigene Wahl wird nicht überschrieben.
    // Vorbelegt wird nur ein Eintrag, den die Auswahlliste auch kennt.
    if (!newTicket.title.trim() && !newTicket.description.trim()) {
      const ich = String(benutzerEmail || '').toLowerCase();
      const eintrag = (availableEmails || []).find(e => String(e).toLowerCase() === ich);
      setNewTicket(prev => ({ ...prev, assignedTo: aktiverReiter === 'meine' && eintrag ? eintrag : '' }));
      setPendingCreateTicketFiles([]);
    }
    setShowCreateTicketForm(true);
  };

  return (
    <div className="mobil-start">
      <header className="mobil-kopf">
        {/* #566: Produktname als Wortmarke wie in der Kopfzeile am Arbeitsplatz */}
        <h1 aria-label="KayCee Ticket"><Wortmarke /></h1>
        {/* #512: der KayCee-Assistent, für alle Rollen (nur mit searchAi) */}
        {kiSucheAn && (
          <button type="button" className="mobil-kopf-knopf" onClick={oeffneKiSuche}
            aria-label={t('assistent.oeffnen', 'KayCee-Assistent öffnen')} title={t('assistent.oeffnen', 'KayCee-Assistent öffnen')}>
            <KayCeeKopf size={24} />
          </button>
        )}
        <button type="button" className="mobil-kopf-knopf" onClick={aktualisieren} disabled={aktualisiert}
          aria-label={t('mobile.refresh', 'Aktualisieren')} title={t('mobile.refresh', 'Aktualisieren')}>
          <RefreshCw size={22} className={aktualisiert ? 'animate-spin' : undefined} />
        </button>
        <button type="button" className="mobil-kopf-knopf" onClick={() => setEinstellungenOffen(true)}
          aria-label={t('mobile.settings', 'Einstellungen')} title={t('mobile.settings', 'Einstellungen')}>
          <Settings size={22} />
        </button>
      </header>

      <nav className="mobil-reiter" role="tablist" aria-label={t('mobile.tabsLabel', 'Bereiche')}>
        {reiterListe.map(reiter => (
          <button
            key={reiter}
            type="button"
            role="tab"
            id={`mobil-reiter-${reiter}`}
            aria-controls={`mobil-seite-${reiter}`}
            aria-selected={aktiverReiter === reiter ? 'true' : 'false'}
            className={`mobil-reiter-knopf${aktiverReiter === reiter ? ' aktiv' : ''}`}
            onClick={() => { angefasstRef.current = true; springeZu(reiter); }}
          >
            <span>{titel[reiter]}</span>
            {bereit && <span className="mobil-reiter-zahl">{anzahl[reiter]}</span>}
          </button>
        ))}
      </nav>

      <div
        className="mobil-seiten"
        ref={seitenRef}
        onScroll={beimScrollen}
        onTouchStart={() => { angefasstRef.current = true; }}
      >
        <section className="mobil-seite" id="mobil-seite-heute" role="tabpanel" aria-labelledby="mobil-reiter-heute">
          <CompactEventList
            events={events}
            onEventClick={beimTerminKlick}
            refreshKey={eventRefreshKey}
            currentUserEmail={benutzerEmail}
            linkedCalendarEventIds={linkedCalendarEventIds}
            calendarEventTicketStatus={calendarEventTicketStatus}
            plannedTicketEntries={plannedTicketEntries}
          />
        </section>
        <section className="mobil-seite" id="mobil-seite-meine" role="tabpanel" aria-labelledby="mobil-reiter-meine">
          <Suchfeld wert={sucheMeine} onChange={setSucheMeine} label={t('mobile.searchMine', 'Meine Tickets durchsuchen')} />
          <DashboardTicketList
            tickets={myTickets}
            searchFilter={sucheMeine}
            onTicketClick={oeffneTicket}
            onTicketUpdate={aktualisieren}
            useAssigneePerspective={true}
            status={ticketsStatus}
            onRetry={aktualisieren}
            touchModus
          />
        </section>
        {istManager && (
        <section className="mobil-seite" id="mobil-seite-pool" role="tabpanel" aria-labelledby="mobil-reiter-pool">
          <Suchfeld wert={suchePool} onChange={setSuchePool} label={t('mobile.searchPool', 'Pool-Tickets durchsuchen')} />
          <DashboardTicketList
            tickets={tickets}
            searchFilter={suchePool}
            onTicketClick={oeffneTicket}
            onTicketUpdate={aktualisieren}
            status={ticketsStatus}
            onRetry={aktualisieren}
            touchModus
          />
        </section>
        )}
      </div>

      {kiOffen && kiSucheAn && (
        <AssistentChat mobil onClose={schliesseKiSuche} onOeffneTicket={(k) => oeffneTicket({ id: k.ticketId })} />
      )}

      {!offenesTicket && !kiOffen && !terminOffen && (
        <button type="button" className="mobil-neu" onClick={neuesTicket}
          aria-label={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')} title={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}>
          <Plus size={28} />
        </button>
      )}
      <CreateTicketModal anlegen={anlegen} kameraKnopf />
      <MobilEinstellungen offen={einstellungenOffen} onClose={() => setEinstellungenOffen(false)} benutzer={currentUser} />

      {offenesTicket && (
        <MobilTicketDetail
          ticketId={offenesTicket}
          currentUser={currentUser}
          onClose={schliesseTicket}
        />
      )}

      {termin && terminOffen && (
        <MobilTermin event={termin} onClose={schliesseTermin} />
      )}
    </div>
  );
};

export default MobilStart;
